/**
 * LineChart Component
 *
 * Recharts LineChart wrapper with Safaqat 24 styling.
 */

import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { formatAmount } from '../../utils/numberFormat';

export interface LineChartData {
  name: string;
  [key: string]: string | number;
}

export interface LineConfig {
  dataKey: string;
  name: string;
  color: string;
  strokeWidth?: number;
}

export interface LineChartProps {
  data: LineChartData[];
  lines: LineConfig[];
  height?: number;
  showGrid?: boolean;
  showLegend?: boolean;
  xAxisDataKey?: string;
  className?: string;
}

export default function LineChart({
  data,
  lines,
  height = 300,
  showGrid = true,
  showLegend = true,
  xAxisDataKey = 'name',
  className = '',
}: LineChartProps) {
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-[var(--pv-color-surface-strong)] p-3 rounded-xl shadow-lg border border-[var(--pv-border-soft)]">
          <p className="text-sm font-medium text-[var(--pv-color-text)] mb-2">{label}</p>
          {payload.map((entry: any, index: number) => (
            <p
              key={index}
              className="text-sm"
              style={{ color: entry.color }}
            >
              {entry.name}: {formatAmount(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className={className} style={{ direction: 'ltr' }}>
      <ResponsiveContainer width="100%" height={height}>
        <RechartsLineChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
          {showGrid && (
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="var(--pv-border-soft)"
              vertical={false}
            />
          )}
          <XAxis
            dataKey={xAxisDataKey}
            tick={{ fontSize: 12, fill: 'var(--pv-color-text-muted)' }}
            axisLine={{ stroke: 'var(--pv-border-soft)' }}
            tickLine={false}
          />
          <YAxis
            tick={{ fontSize: 12, fill: 'var(--pv-color-text-muted)' }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(value) => formatAmount(value)}
          />
          <Tooltip content={<CustomTooltip />} />
          {showLegend && (
            <Legend
              wrapperStyle={{ paddingTop: 20 }}
              formatter={(value) => (
                <span className="text-sm text-[var(--pv-color-text)]">{value}</span>
              )}
            />
          )}
          {lines.map((line) => (
            <Line
              key={line.dataKey}
              type="monotone"
              dataKey={line.dataKey}
              name={line.name}
              stroke={line.color}
              strokeWidth={line.strokeWidth || 2}
              dot={false}
              activeDot={{ r: 6, strokeWidth: 2 }}
            />
          ))}
        </RechartsLineChart>
      </ResponsiveContainer>
    </div>
  );
}
